import React, { useState, useEffect } from 'react';
import { Company } from '../types/billing';
import { pdf } from '@react-pdf/renderer';
import QuotationPDF from '../services/quotationPDFGenerator';

interface QuotationItem {
  srNo: number;
  description: string;
  qty: number;
  uom: string;
  rate: number;
  totalAmount: number;
}

interface QuotationData {
  quotationNumber: string;
  date: string;
  clientName: string;
  clientAddress: string;
  attention: string;
  subject: string;
  salutation: string;
  introductoryText: string;
  items: QuotationItem[];
  subTotal: number;
  gstRate: number;
  gstAmount: number;
  
  grandTotal: number;
  amountInWords: string;
  bankDetails: {
    accountNumber: string;
    ifscCode: string;
    bankName: string;
  };
  termsAndConditions: string[];
  closingStatement: string;
  companyName: string;
  registeredOffice: string;
  contactNumbers: string[];
  email: string;
  website: string;
}

interface QuotationGeneratorProps {
  companies: Company[];
  onSave: (quotationData: QuotationData) => void;
  onClose: () => void;
  initialData?: Partial<QuotationData>;
  isSaving?: boolean;
}

const QuotationGenerator: React.FC<QuotationGeneratorProps> = ({
  companies,
  onSave,
  onClose,
  initialData,
  isSaving = false
}) => {
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendMethod, setSendMethod] = useState<'whatsapp' | 'email'>('whatsapp');
  const [manualContact, setManualContact] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  const [quotationData, setQuotationData] = useState<QuotationData>({
    quotationNumber: `AVIS/QTN/${new Date().getFullYear()}/${String(new Date().getMonth() + 1).padStart(2, '0')}/${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
    date: new Date().toLocaleDateString('en-GB'),
    clientName: '',
    clientAddress: '',
    attention: 'Mr',
    subject: 'Quotation for NDT Services',
    salutation: 'Dear Sir',
    introductoryText: 'This is reference to our discussion of Quotation for NDT Services in your organization, we are submitting our quotation.',
    items: [
      { srNo: 1, description: 'NDT Testing Services', qty: 1, uom: 'NOS', rate: 0, totalAmount: 0 }
    ],
    subTotal: 0,
    gstRate: 18,
    gstAmount: 0,
    grandTotal: 0,
    amountInWords: '',
    bankDetails: {
      accountNumber: '610000000061452',
      ifscCode: 'SRCB0000400',
      bankName: 'SARASWAT CO-OPERATIVE BANK LTD.'
    },
    termsAndConditions: [
      'Detailed work order required to start actual work.',
      'Payment 50% advance and remaining after completion of work.',
      'Subject to Ahmednagar Jurisdiction only.',
      'All payments are delayed more than 30 days from the date of submission of invoice along with test reports then interest at the rate of 24% will be charged extra on all over dues.',
      'I.S.O. Certificate will be forwarded immediately.'
    ],
    closingStatement: 'We hope will find above rated offer most competitive & assuring the best our service at all times. Now look forward to hearing from you and await your valued work order.',
    companyName: 'AVI\'S ENGINEERING & CONSULTANCY',
    registeredOffice: 'SR No-4/1A; plot on-: 17 SHREE SWAMI SAMARTH NIWAS, Bistabag, Savedi, Ahilyanagar',
    contactNumbers: ['7887985350', ''],
    email: 'avis.engineering2025@gmail.com',
    website: 'www.avisengineering.com',
    ...initialData
  });

  const [selectedClient, setSelectedClient] = useState<Company | null>(
    companies[0] || null
  );

  useEffect(() => {
    if (selectedClient) {
      setQuotationData(prev => ({
        ...prev,
        clientName: selectedClient.name,
        clientAddress: selectedClient.address,
      }));
    }
  }, [selectedClient]);

  const formatNumber = (num: number): string => {
    return (Math.round(num * 100) / 100).toFixed(2);
  };

  const calculateTotals = (items: QuotationItem[], gstRate: number) => {
    const subTotal = items.reduce((sum, item) => sum + item.totalAmount, 0);
    const gstAmount = subTotal * (gstRate / 100);
    const grandTotal = subTotal + gstAmount;
    return { subTotal, gstAmount, grandTotal };
  };

  const convertToWords = (num: number): string => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    if (num === 0) return 'Zero';
    
    let integerPart = Math.floor(num);
    const decimalPart = Math.round((num - integerPart) * 100);
    
    const convertHundreds = (n: number): string => {
      let result = '';
      
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        n = 0;
      }
      
      if (n > 0) {
        result += ones[n] + ' ';
      }
      
      return result.trim();
    };
    
    let result = '';
    
    if (integerPart >= 100000) {
      const lakhs = Math.floor(integerPart / 100000);
      result += convertHundreds(lakhs) + ' Lakh ';
      integerPart %= 100000;
    }
    
    if (integerPart >= 1000) {
      const thousands = Math.floor(integerPart / 1000);
      result += convertHundreds(thousands) + ' Thousand ';
      integerPart %= 1000;
    }
    
    if (integerPart > 0) {
      result += convertHundreds(integerPart) + ' ';
    }
    
    result += 'Rupees';
    
    if (decimalPart > 0) {
      result += ' ' + convertHundreds(decimalPart) + ' Paise';
    }
    
    return result.trim() + ' Only';
  };

  const handleItemChange = (index: number, key: keyof QuotationItem, value: string | number) => {
    const updatedItems = [...quotationData.items];
    const itemToUpdate = updatedItems[index];
    
    if (key === 'qty' || key === 'rate') {
      const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      (itemToUpdate as any)[key] = numValue;
      itemToUpdate.totalAmount = itemToUpdate.qty * itemToUpdate.rate;
    } else {
      (itemToUpdate as any)[key] = value;
    }

    const totals = calculateTotals(updatedItems, quotationData.gstRate);
    setQuotationData(prev => ({
      ...prev,
      items: updatedItems,
      ...totals,
      amountInWords: convertToWords(totals.grandTotal)
    }));
  };

  const addItem = () => {
    const newItem: QuotationItem = {
      srNo: quotationData.items.length + 1,
      description: '',
      qty: 1,
      uom: 'NOS',
      rate: 0,
      totalAmount: 0
    };
    setQuotationData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const removeItem = (index: number) => {
    if (quotationData.items.length > 1) {
      const updatedItems = quotationData.items.filter((_, i) => i !== index);
      const renumberedItems = updatedItems.map((item, i) => ({
        ...item,
        srNo: i + 1
      }));
      const totals = calculateTotals(renumberedItems, quotationData.gstRate);
      setQuotationData(prev => ({
        ...prev,
        items: renumberedItems,
        ...totals,
        amountInWords: convertToWords(totals.grandTotal)
      }));
    }
  };

  const handleSave = () => {
    // Validation
    const errors: string[] = [];
    
    if (!quotationData.clientName.trim()) {
      errors.push('Please enter client name');
    }
    
    if (!quotationData.clientAddress.trim()) {
      errors.push('Please enter client address');
    }
    
    if (quotationData.items.some(item => !item.description.trim())) {
      errors.push('Please fill in all item descriptions');
    }
    
    if (quotationData.items.some(item => item.rate <= 0)) {
      errors.push('Please enter valid rates for all items');
    }
    
    if (errors.length > 0) {
      alert(errors.join('\n'));
      return;
    }

    onSave(quotationData);
  };

  const handleDownloadPDF = async () => {
    try {
      // Validate data before generating PDF
      if (!quotationData.clientName.trim() || !quotationData.clientAddress.trim()) {
        alert('Please fill in client name and address before generating PDF.');
        return;
      }
      
      if (quotationData.items.some(item => !item.description.trim() || item.rate <= 0)) {
        alert('Please fill in all item descriptions and valid rates before generating PDF.');
        return;
      }

      const blob = await pdf(<QuotationPDF quotationData={quotationData} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quotation_${quotationData.quotationNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please check your data and try again. If the problem persists, try refreshing the page.');
    }
  };

  const handleSendQuotation = async () => {
    try {
      // Validate data before sending
      if (!quotationData.clientName.trim() || !quotationData.clientAddress.trim()) {
        alert('Please fill in client name and address before sending quotation.');
        return;
      }
      
      if (quotationData.items.some(item => !item.description.trim() || item.rate <= 0)) {
        alert('Please fill in all item descriptions and valid rates before sending quotation.');
        return;
      }

      setShowSendModal(true);
    } catch (error) {
      console.error('Error preparing to send quotation:', error);
      alert('Failed to prepare quotation for sending. Please try again.');
    }
  };

  const getContactInfo = () => {
    const selectedCompany = companies.find(c => c.name === quotationData.clientName);
    if (selectedCompany) {
      return {
        email: selectedCompany.email,
        phone: selectedCompany.phone
      };
    }
    return { email: '', phone: '' };
  };

  const handleSendViaWhatsApp = async () => {
    try {
      setIsSending(true);
      const contactInfo = getContactInfo();
      const phoneNumber = manualContact || contactInfo.phone;
      
      if (!phoneNumber) {
        alert('Please enter a phone number for WhatsApp.');
        return;
      }

      // Clean phone number (remove spaces, dashes, etc.)
      const cleanPhone = phoneNumber.replace(/[\s\-()]/g, '');
      const whatsappNumber = cleanPhone.startsWith('+') ? cleanPhone : `+91${cleanPhone}`;
      
      // Create WhatsApp message
      const message = `Hello ${quotationData.clientName},\n\nPlease find attached our quotation for your NDT services inquiry.\n\nQuotation Number: ${quotationData.quotationNumber}\nTotal Amount: ₹${quotationData.grandTotal.toLocaleString()}\n\nThank you for considering our services.\n\nBest regards,\nAVI'S ENGINEERING & CONSULTANCY`;
      
      // Open WhatsApp Web with the message
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      setShowSendModal(false);
      alert('WhatsApp opened! Please attach the PDF manually.');
    } catch (error) {
      console.error('Error sending via WhatsApp:', error);
      alert('Failed to open WhatsApp. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendViaEmail = async () => {
    try {
      setIsSending(true);
      const contactInfo = getContactInfo();
      const email = manualContact || contactInfo.email;
      
      if (!email) {
        alert('Please enter an email address.');
        return;
      }

      // Create mailto link with subject and body
      const subject = `Quotation ${quotationData.quotationNumber} - NDT Services`;
      const body = `Dear ${quotationData.clientName},\n\nPlease find attached our quotation for your NDT services inquiry.\n\nQuotation Number: ${quotationData.quotationNumber}\nTotal Amount: ₹${quotationData.grandTotal.toLocaleString()}\n\nThank you for considering our services.\n\nBest regards,\nAVI'S ENGINEERING & CONSULTANCY`;
      
      const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoUrl, '_blank');
      
      setShowSendModal(false);
      alert('Email client opened! Please attach the PDF manually.');
    } catch (error) {
      console.error('Error sending via email:', error);
      alert('Failed to open email client. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-6 border w-full max-w-6xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Generate Quotation</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <div className="space-y-6">
            {/* Client Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Client
              </label>
              <select
                value={selectedClient?.id || ''}
                onChange={(e) => {
                  const client = companies.find(c => c.id === e.target.value);
                  setSelectedClient(client || null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avis-blue"
              >
                <option value="">Select a client</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Quotation Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quotation Number
                </label>
                <input
                  type="text"
                  value={quotationData.quotationNumber}
                  onChange={(e) => setQuotationData(prev => ({ ...prev, quotationNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avis-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  type="text"
                  value={quotationData.date}
                  onChange={(e) => setQuotationData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avis-blue"
                />
              </div>
            </div>

            {/* Client Information */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Name
              </label>
              <input
                type="text"
                value={quotationData.clientName}
                onChange={(e) => setQuotationData(prev => ({ ...prev, clientName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avis-blue"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Address
              </label>
              <textarea
                value={quotationData.clientAddress}
                onChange={(e) => setQuotationData(prev => ({ ...prev, clientAddress: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avis-blue"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attention
                </label>
                <input
                  type="text"
                  value={quotationData.attention}
                  onChange={(e) => setQuotationData(prev => ({ ...prev, attention: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avis-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={quotationData.subject}
                  onChange={(e) => setQuotationData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avis-blue"
                />
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Items</h4>
                <button
                  onClick={addItem}
                  className="px-4 py-2 bg-avis-blue text-white rounded-md hover:bg-blue-700"
                >
                  Add Item
                </button>
              </div>
              
              <div className="space-y-4">
                {quotationData.items.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-1">
                        <input
                          type="number"
                          value={item.srNo}
                          disabled
                          className="w-full px-2 py-1 border border-gray-300 rounded text-center"
                        />
                      </div>
                      <div className="col-span-4">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Description"
                          className="w-full px-2 py-1 border border-gray-300 rounded"
                        />
                      </div>
                      <div className="col-span-1">
                        <input
                          type="number"
                          value={item.qty}
                          onChange={(e) => handleItemChange(index, 'qty', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-center"
                        />
                      </div>
                      <div className="col-span-1">
                        <input
                          type="text"
                          value={item.uom}
                          onChange={(e) => handleItemChange(index, 'uom', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-center"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                          placeholder="Rate"
                          className="w-full px-2 py-1 border border-gray-300 rounded"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          value={item.totalAmount}
                          disabled
                          className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50"
                        />
                      </div>
                      <div className="col-span-1">
                        <button
                          onClick={() => removeItem(index)}
                          disabled={quotationData.items.length === 1}
                          className="w-full px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Rate (%)
                  </label>
                  <input
                    type="number"
                    value={quotationData.gstRate}
                    onChange={(e) => {
                      const rate = parseFloat(e.target.value) || 0;
                      const totals = calculateTotals(quotationData.items, rate);
                      setQuotationData(prev => ({
                        ...prev,
                        gstRate: rate,
                        ...totals,
                        amountInWords: convertToWords(totals.grandTotal)
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avis-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sub Total
                  </label>
                  <input
                    type="number"
                    value={quotationData.subTotal}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Amount
                  </label>
                  <input
                    type="number"
                    value={quotationData.gstAmount}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Grand Total
                  </label>
                  <input
                    type="number"
                    value={quotationData.grandTotal}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-semibold"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount in Words
                </label>
                <textarea
                  value={quotationData.amountInWords}
                  onChange={(e) => setQuotationData(prev => ({ ...prev, amountInWords: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avis-blue"
                />
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Preview</h4>
            <div className="text-xs space-y-2 text-gray-600">
              <div className="text-center">
                <div className="text-lg font-bold">AVI'S ENGINEERING & CONSULTANCY</div>
                <div className="text-sm">NDT SERVICES</div>
                <div className="text-xs mt-2">An ISO 9001:2015 Certified Company</div>
              </div>
              <div className="text-center mt-4">
                <div className="text-lg font-bold">QUOTATION</div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <div><strong>Quotation No:</strong> {quotationData.quotationNumber}</div>
                  <div><strong>GST NO:</strong> 27FLTPM3802F1ZU</div>
                </div>
                <div>
                  <div><strong>Date:</strong> {quotationData.date}</div>
                </div>
              </div>
              <div className="mt-4">
                <div><strong>Client Name:</strong> {quotationData.clientName}</div>
                <div><strong>Client Address:</strong> {quotationData.clientAddress}</div>
                <div><strong>Attention:</strong> {quotationData.attention}</div>
                <div><strong>Subject:</strong> {quotationData.subject}</div>
              </div>
              <div className="mt-4">
                <div><strong>Salutation:</strong> {quotationData.salutation}</div>
                <div className="mt-2">{quotationData.introductoryText}</div>
              </div>
              <div className="mt-4">
                <table className="w-full text-xs border-collapse border border-gray-400">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-400 px-2 py-1">Sr No</th>
                      <th className="border border-gray-400 px-2 py-1">Description</th>
                      <th className="border border-gray-400 px-2 py-1">Qty</th>
                      <th className="border border-gray-400 px-2 py-1">UOM</th>
                      <th className="border border-gray-400 px-2 py-1">Rate</th>
                      <th className="border border-gray-400 px-2 py-1">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotationData.items.map((item, index) => (
                      <tr key={index}>
                        <td className="border border-gray-400 px-2 py-1 text-center">{item.srNo}</td>
                        <td className="border border-gray-400 px-2 py-1">{item.description}</td>
                        <td className="border border-gray-400 px-2 py-1 text-center">{item.qty}</td>
                        <td className="border border-gray-400 px-2 py-1 text-center">{item.uom}</td>
                        <td className="border border-gray-400 px-2 py-1 text-right">{item.rate}</td>
                        <td className="border border-gray-400 px-2 py-1 text-right">{item.totalAmount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-right">
                <div><strong>Sub Total:</strong> ₹{quotationData.subTotal.toLocaleString()}</div>
                <div><strong>GST {quotationData.gstRate}%:</strong> ₹{quotationData.gstAmount.toLocaleString()}</div>
                <div className="bg-purple-100 p-2 rounded"><strong>Grand Total:</strong> ₹{quotationData.grandTotal.toLocaleString()}</div>
              </div>
              <div className="mt-4">
                <div><strong>Amount in Words:</strong> {quotationData.amountInWords}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-avis-blue"
          >
            Cancel
          </button>
          <button
            onClick={handleDownloadPDF}
            className="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            Download PDF
          </button>
          <button
            onClick={handleSendQuotation}
            className="px-6 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            Send Quotation
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 text-sm font-medium text-white bg-avis-blue border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-avis-blue disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Generate Quotation'}
          </button>
        </div>

        {/* Send Quotation Modal */}
        {showSendModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
            <div className="relative top-20 mx-auto p-6 border w-full max-w-md shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Send Quotation</h3>
                <button
                  onClick={() => setShowSendModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Send Method Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Send via
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="whatsapp"
                        checked={sendMethod === 'whatsapp'}
                        onChange={(e) => setSendMethod(e.target.value as 'whatsapp' | 'email')}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">WhatsApp</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="email"
                        checked={sendMethod === 'email'}
                        onChange={(e) => setSendMethod(e.target.value as 'whatsapp' | 'email')}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Email</span>
                    </label>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {sendMethod === 'whatsapp' ? 'Phone Number' : 'Email Address'}
                  </label>
                  <div className="space-y-2">
                    {(() => {
                      const contactInfo = getContactInfo();
                      const hasContact = sendMethod === 'whatsapp' ? contactInfo.phone : contactInfo.email;
                      
                      return (
                        <>
                          {hasContact && (
                            <div className="p-2 bg-gray-50 rounded border">
                              <div className="text-sm text-gray-600">
                                Company {sendMethod === 'whatsapp' ? 'Phone' : 'Email'}: {hasContact}
                              </div>
                            </div>
                          )}
                          <input
                            type={sendMethod === 'whatsapp' ? 'tel' : 'email'}
                            value={manualContact}
                            onChange={(e) => setManualContact(e.target.value)}
                            placeholder={hasContact ? `Or enter ${sendMethod === 'whatsapp' ? 'phone number' : 'email'} manually` : `Enter ${sendMethod === 'whatsapp' ? 'phone number' : 'email'}`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avis-blue"
                          />
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowSendModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendMethod === 'whatsapp' ? handleSendViaWhatsApp : handleSendViaEmail}
                    disabled={isSending}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 disabled:opacity-50"
                  >
                    {isSending ? 'Sending...' : `Send via ${sendMethod === 'whatsapp' ? 'WhatsApp' : 'Email'}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuotationGenerator;
